import { Button, Modal, Table, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { type IFacebookScoredProduct } from '../../reducers/facebook/types';
import { FONT_SIZE } from '../../theme/tokens';

const { Text } = Typography;

const columns: ColumnsType<IFacebookScoredProduct> = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a: IFacebookScoredProduct, b: IFacebookScoredProduct): number =>
      a.name.localeCompare(b.name),
  },
  {
    title: 'Score',
    dataIndex: 'adsScore',
    key: 'adsScore',
    width: 120,
    align: 'right',
    defaultSortOrder: 'descend',
    sorter: (a: IFacebookScoredProduct, b: IFacebookScoredProduct): number =>
      a.adsScore - b.adsScore,
    render: (val: number): React.ReactElement => (
      <Text strong>{val.toFixed(2)}</Text>
    ),
  },
  {
    title: 'UUID',
    dataIndex: 'uuid',
    key: 'uuid',
    width: 300,
    render: (val: string): React.ReactElement => (
      <RouterLink
        to={`/product/${val}`}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e: React.MouseEvent): void => e.stopPropagation()}
        style={{ fontFamily: 'monospace', fontSize: FONT_SIZE.sm }}
      >
        {val}
      </RouterLink>
    ),
  },
];

function ProductPickerModal({
  visible,
  scoredProducts,
  inProcess,
  onSelect,
  onCancel,
}: {
  visible: boolean;
  scoredProducts: IFacebookScoredProduct[] | null;
  inProcess: boolean;
  onSelect: (uuid: string, name: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [selectedRowKey, setSelectedRowKey] = useState<string | undefined>(
    undefined,
  );

  useEffect((): void => {
    if (!visible) {
      setSelectedRowKey(undefined);
    }
  }, [visible]);

  const handleConfirm = (): void => {
    if (!selectedRowKey || !scoredProducts) {
      return;
    }
    const row: IFacebookScoredProduct | undefined = scoredProducts.find(
      (p: IFacebookScoredProduct): boolean => p.uuid === selectedRowKey,
    );
    if (row) {
      onSelect(row.uuid, row.name);
    }
  };

  return (
    <Modal
      title="Select product"
      open={visible}
      onCancel={onCancel}
      width="90vw"
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="select"
          type="primary"
          disabled={!selectedRowKey}
          onClick={handleConfirm}
        >
          Select product
        </Button>,
      ]}
    >
      <Table<IFacebookScoredProduct>
        rowKey="uuid"
        columns={columns}
        dataSource={scoredProducts ?? []}
        loading={inProcess}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
          onChange: (keys: React.Key[]): void => {
            setSelectedRowKey(keys[0] as string | undefined);
          },
        }}
        onRow={(
          record: IFacebookScoredProduct,
        ): React.HTMLAttributes<HTMLTableRowElement> => ({
          style: { cursor: 'pointer' },
          onClick: (): void => setSelectedRowKey(record.uuid),
        })}
      />
    </Modal>
  );
}

export default ProductPickerModal;
